import React, {useEffect, useRef} from 'react';

import {IconX} from '@veneer/core';

import {WFCButton} from '../../../../../WFCButton';
import {useAppSwitcherConfig} from '../../../../context/Config';
import {CategoryMeta, CategoryService, Config, ToolMeta, ToolsMeta} from '../../../../utils/services/types';
import useStyles from '../../useStyles';
import MenuItem from '../MenuItem/MenuItem';

const Item = ({service}: {service: CategoryService | ToolMeta}) => {
    const {currentAppId, loginType, currentLanguage, environment, wexDomains} = useAppSwitcherConfig();

    const getUrl = () => {
        let url = service.launchUrl;
        /* istanbul ignore if */
        if (service.details?.redirectUrls) {
            /* istanbul ignore next */
            url = service.details.redirectUrls[loginType] || service.details.redirectUrls.default;

            // If URL belongs to a specific service replace the baseDomain with the correct environment domain.
            if (url.includes('{baseDomain}')) {
                /* istanbul ignore else */
                if ('baseDomains' in service && service.baseDomains) {
                    /* istanbul ignore next */
                    const baseDomain = service.baseDomains?.[environment] || service.baseDomains.default;
                    /* istanbul ignore next */
                    if (!baseDomain) {
                        // eslint-disable-next-line i18next/no-literal-string
                        throw Error('No base domain exists.');
                    }
                    // eslint-disable-next-line i18next/no-literal-string
                    url = url.replaceAll('{baseDomain}', baseDomain);
                }
            }

            // Check for required arguments if there are any.
            // Use the fallback url if there's any we don't currently support.
            /* istanbul ignore next */
            if (url.includes('{') || url.includes('}')) {
                url = service.launchUrl;
            }
        }
        return url;
    };

    let url = getUrl();

    // If the URL doesn't start with http assume it is a WEX base domain and prepend their environment domain.
    if (!url.startsWith('http')) {
        url = wexDomains[environment] + url;
    }

    // Clean up any accidental double slashes in the resultant URL.
    url = url.replaceAll(/(?<!:)\/\//g, '/');

    return (
        <MenuItem
            component="anchor"
            active={service.name === currentAppId}
            key={service.name}
            label={service.displayName[currentLanguage].value}
            icon={service.icon}
            href={url}
        />
    );
};

const SubMenu = ({id, onClose}: {id: keyof Config; onClose: () => void}) => {
    const {serviceMeta, i18n, currentLanguage} = useAppSwitcherConfig();
    const styles = useStyles(true, true);

    const items = Object.values<CategoryMeta | ToolsMeta>(serviceMeta[id]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = ref.current;

        // Should ever actually happen just to satisfy typescript.
        /* istanbul ignore next */
        if (!element) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            // eslint-disable-next-line i18next/no-literal-string
            const elements = element.querySelectorAll('a');
            if (e.key === 'Tab' && e.shiftKey) {
                /* istanbul ignore else */
                if (document.activeElement === elements[0]) {
                    e.stopPropagation();
                    e.preventDefault();
                    onClose();
                }
                return;
            }
            if (e.key === 'Tab') {
                if (element.contains(document.activeElement)) {
                    if (document.activeElement === elements[elements.length - 1]) {
                        // Prevent default so that the onClose handler can choose what to focus next.
                        e.stopPropagation();
                        e.preventDefault();
                        onClose();
                    }
                } else {
                    e.stopPropagation();
                    e.preventDefault();
                    elements[0]?.focus();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <div
            className={styles.wrapper}
            ref={ref}
            data-testid="AppSwitcher--menu--level-2"
        >
            <div className={styles.heading}>
                <h3 className="subtitle-regular">{id === 'categories' ? i18n.services : i18n.tools}</h3>

                {/* Just here to maintain consist sizing between the side menu and sub menu. */}
                <div className={styles.hideButton}>
                    <WFCButton>
                        <IconX customStyle={{fill: 'white', color: 'white'}} />
                    </WFCButton>
                </div>
            </div>
            <div className={styles.inner}>
                <nav
                    aria-label={i18n.ariaLabels[id].toggle}
                    key={items.length}
                    className={styles.nav}
                >
                    {items.map(item => (
                        <div
                            className={styles.group}
                            key={item.title.en.name}
                        >
                            <p className={`overline-regular ${styles.overline}`}>{item.title[currentLanguage].value}</p>
                            {item.services.map(service => (
                                <Item
                                    key={service.name}
                                    service={service}
                                />
                            ))}
                        </div>
                    ))}
                </nav>
            </div>
        </div>
    );
};

export default SubMenu;
